import { lazy, Suspense, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { MapPin, Plus, Check, Loader, Pencil, Trash2, Star } from 'lucide-react';
import {
  listAddresses, createAddress, updateAddress, removeAddress, setDefaultAddress,
} from '../../../services/address.api';
import AddressAutocomplete from '../../../Components/Common/Map/AddressAutocomplete';
import MapSkeleton from '../../../Components/Common/Map/MapSkeleton';
import { DEFAULT_CENTER } from '../../../services/mapbox';

// mapbox-gl is heavy — only pull it in once the address form is actually open.
const MapboxPicker = lazy(() => import('../../../Components/Common/Map/MapboxPicker'));

const MAX_ADDRESSES = 10;

const emptyForm = {
  label: 'Home',
  address: '',
  houseNumber: '',
  apartment: '',
  phone: '',
  deliveryInstructions: '',
};

const field = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 8,
  border: '1.5px solid #e5e7eb',
  fontSize: 13,
  fontFamily: "'Inter', sans-serif",
  background: '#f9fafb',
  outline: 'none',
  boxSizing: 'border-box',
};

export default function AddressBookTab() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null | 'new' | address._id
  const [form, setForm] = useState(emptyForm);
  const [picked, setPicked] = useState(DEFAULT_CENTER);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null); // address._id being deleted/defaulted

  const load = async () => {
    try {
      setLoading(true);
      setAddresses(await listAddresses());
    } catch (e) {
      toast.error(e?.message || 'Could not load your saved addresses.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startAdd = () => {
    setForm(emptyForm);
    setPicked(DEFAULT_CENTER);
    setEditingId('new');
  };

  const startEdit = (address) => {
    setForm({
      label: address.label || '',
      address: address.address || '',
      houseNumber: address.houseNumber || '',
      apartment: address.apartment || '',
      phone: address.phone || '',
      deliveryInstructions: address.deliveryInstructions || '',
    });
    setPicked({
      lat: Number(address.latitude) || DEFAULT_CENTER.lat,
      lng: Number(address.longitude) || DEFAULT_CENTER.lng,
    });
    setEditingId(address._id);
  };

  const cancelForm = () => setEditingId(null);

  const handlePlacePicked = (place) => {
    setPicked({ lat: place.lat, lng: place.lng });
    if (place.address) setForm((f) => ({ ...f, address: place.address }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.label.trim() || !form.address.trim()) {
      toast.error('Label and address are required.');
      return;
    }
    try {
      setSaving(true);
      if (editingId === 'new') {
        const created = await createAddress({
          ...form,
          latitude: picked.lat,
          longitude: picked.lng,
        });
        setAddresses((prev) => [...prev, created]);
        toast.success('Address added.');
      } else {
        const updated = await updateAddress(editingId, {
          ...form,
          latitude: picked.lat,
          longitude: picked.lng,
        });
        setAddresses((prev) => prev.map((a) => (a._id === editingId ? updated : a)));
        toast.success('Address updated.');
      }
      setEditingId(null);
    } catch (err) {
      toast.error(err?.message || 'Could not save the address.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (address) => {
    if (!window.confirm(`Delete "${address.label}"? This can't be undone.`)) return;
    try {
      setBusyId(address._id);
      await removeAddress(address._id);
      setAddresses((prev) => prev.filter((a) => a._id !== address._id));
      toast.success('Address deleted.');
    } catch (err) {
      toast.error(err?.message || 'Could not delete that address.');
    } finally {
      setBusyId(null);
    }
  };

  const handleSetDefault = async (address) => {
    if (address.isDefault) return;
    try {
      setBusyId(address._id);
      await setDefaultAddress(address._id);
      setAddresses((prev) => prev.map((a) => ({ ...a, isDefault: a._id === address._id })));
      toast.success('Default address updated.');
    } catch (err) {
      toast.error(err?.message || 'Could not set that as default.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="prof-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <p className="prof-card-label" style={{ margin: 0 }}>Saved Addresses</p>
        {!loading && editingId === null && addresses.length < MAX_ADDRESSES && (
          <button type="button" onClick={startAdd} style={addBtnStyle}>
            <Plus size={13} /> Add address
          </button>
        )}
      </div>

      {loading && <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>Loading your addresses…</p>}

      {!loading && addresses.length === 0 && editingId === null && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <MapPin size={28} color="#d1d5db" />
          <p style={{ margin: '10px 0 14px', fontSize: 13, color: '#9ca3af' }}>You haven't saved any addresses yet.</p>
          <button type="button" onClick={startAdd} style={addBtnStyle}>
            <Plus size={13} /> Add your first address
          </button>
        </div>
      )}

      {!loading && addresses.map((address) => (
        <div
          key={address._id}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px',
            marginBottom: 10, borderRadius: 10,
            border: address.isDefault ? '1.5px solid #0d6efd' : '1.5px solid #e5e7eb',
            background: address.isDefault ? '#eff6ff' : '#f9fafb',
          }}
        >
          <MapPin size={16} color="#6b7280" style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111827' }}>
              {address.label}
              {address.isDefault && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#059669' }}>DEFAULT</span>}
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#6b7280' }}>
              {[address.houseNumber, address.apartment, address.address].filter(Boolean).join(', ')}
            </p>
            {address.phone && <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9ca3af' }}>{address.phone}</p>}
            {address.deliveryInstructions && (
              <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                {address.deliveryInstructions}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
            {!address.isDefault && (
              <button
                type="button"
                title="Set as default"
                onClick={() => handleSetDefault(address)}
                disabled={busyId === address._id}
                style={iconBtnStyle}
              >
                {busyId === address._id ? <Loader size={13} className="prof-spin" /> : <Star size={13} />}
              </button>
            )}
            <button type="button" title="Edit" onClick={() => startEdit(address)} style={iconBtnStyle}>
              <Pencil size={13} />
            </button>
            <button
              type="button"
              title="Delete"
              onClick={() => handleDelete(address)}
              disabled={busyId === address._id}
              style={{ ...iconBtnStyle, color: '#dc2626' }}
            >
              {busyId === address._id ? <Loader size={13} className="prof-spin" /> : <Trash2 size={13} />}
            </button>
          </div>
        </div>
      ))}

      {editingId !== null && (
        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111827' }}>
            {editingId === 'new' ? 'Add a new address' : 'Edit address'}
          </p>

          <input style={field} value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="Label (Home, Office…)" maxLength={50} required />

          <AddressAutocomplete
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onSelect={handlePlacePicked}
            proximity={picked}
            placeholder="Search for the address…"
            className=""
          />

          <Suspense fallback={<MapSkeleton height={260} />}>
            <MapboxPicker value={picked} onPick={handlePlacePicked} height={260} />
          </Suspense>

          <div style={{ display: 'flex', gap: 8 }}>
            <input style={field} value={form.houseNumber} onChange={(e) => setForm((f) => ({ ...f, houseNumber: e.target.value }))} placeholder="House / road" maxLength={50} />
            <input style={field} value={form.apartment} onChange={(e) => setForm((f) => ({ ...f, apartment: e.target.value }))} placeholder="Apartment / floor" maxLength={50} />
          </div>
          <input style={field} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Contact phone (optional)" />
          <input style={field} value={form.deliveryInstructions} onChange={(e) => setForm((f) => ({ ...f, deliveryInstructions: e.target.value }))} placeholder="Delivery instructions (optional)" maxLength={300} />

          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" disabled={saving} style={saveBtnStyle(saving)}>
              {saving ? <Loader size={13} className="prof-spin" /> : <Check size={13} />}
              {saving ? 'Saving…' : 'Save address'}
            </button>
            <button type="button" onClick={cancelForm} style={cancelBtnStyle}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

const addBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '8px 14px', borderRadius: 8, border: '1.5px solid #e5e7eb',
  background: '#fff', fontSize: 12, fontWeight: 600, color: '#374151',
  cursor: 'pointer', fontFamily: "'Inter', sans-serif",
};

const iconBtnStyle = {
  width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
  borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff',
  color: '#374151', cursor: 'pointer',
};

const saveBtnStyle = (saving) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '9px 16px', borderRadius: 8, border: 'none',
  background: saving ? '#9ca3af' : '#0d6efd', color: '#fff', fontSize: 13, fontWeight: 700,
  cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Inter', sans-serif",
});

const cancelBtnStyle = {
  padding: '9px 16px', borderRadius: 8, border: '1.5px solid #e5e7eb',
  background: '#fff', fontSize: 13, fontWeight: 600, color: '#6b7280',
  cursor: 'pointer', fontFamily: "'Inter', sans-serif",
};
